/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

import _ from 'lodash';
import React from 'react';
import { observer, inject } from 'mobx-react';
import { makeObservable, runInAction, action } from 'mobx';
import TimeAgo from 'react-timeago';
import { Header, Dropdown, Label } from 'semantic-ui-react';
import { withRouter } from '../../helpers/routing';

import { displayError } from '../../helpers/notification';
import getUIState from '../workflow-common/component-states/WorkflowCommonCardState';
import WorkflowTemplateCardTabs from './WorkflowTemplateCardTabs';

// expected props
// - template - a WorkflowTemplate model instance (via props)
// - v - the selected version number, will default to latest or existing state (via props)
// - userDisplayName (via injection)
// - location (from react router)
class WorkflowTemplateCard extends React.Component {
  constructor(props) {
    super(props);
    makeObservable(this, {
      handleOnVersionChange: action,
    });

    runInAction(() => {
      const state = this.getState();
      const versionSpecified = !_.isNil(this.props.v);
      let v;

      if (versionSpecified) {
        v = this.props.v;
      } else if (state.versionNumber === -1) {
        v = this.props.template.latest.v;
      } else {
        v = state.versionNumber;
      }
      state.setVersionNumber(v);
    });
  }

  getState() {
    return getUIState(this.getTemplate().id);
  }

  selectedVersionNumber() {
    return this.getState().versionNumber;
  }

  getTemplate() {
    return this.props.template;
  }

  getTemplateVersion() {
    const template = this.getTemplate();
    const v = this.selectedVersionNumber();
    const version = template.getVersion(v);
    if (_.isEmpty(version)) {
      // This is an error
      displayError(`Version ${v} of this workflow template is not valid`);
      return {};
    }

    return version;
  }

  getUserDisplayNameService() {
    return this.props.userDisplayName;
  }

  handleOnVersionChange = ({ value = 1 }) => {
    this.getState().setVersionNumber(value);
  };

  render() {
    const templateVersion = this.getTemplateVersion();
    const { id, v, title, createdAt, createdBy } = templateVersion;
    const displayNameService = this.getUserDisplayNameService();
    const isSystem = displayNameService.isSystem(createdBy);
    const by = () => (isSystem ? '' : <span className="ml1">by {displayNameService.getDisplayName(createdBy)}</span>);

    return (
      <>
        <Label attached="top left">Template</Label>
        <div className="flex mb1">
          <Header as="h3" color="grey" className="mt0 flex-auto ellipsis">
            {title}
            <Header.Subheader className="fs-9">
              created <TimeAgo date={createdAt} /> {by()}
            </Header.Subheader>
          </Header>
          <div className="ml1">
            <span className="ellipsis pr1 fs-9 breakout color-grey">{id}</span> {this.renderVersionDropdown(v)}
          </div>
        </div>
        {this.renderMainTabs(templateVersion)}
      </>
    );
  }

  renderVersionDropdown(currentVersion) {
    const template = this.getTemplate();
    const versions = template.versionNumbers;
    const options = _.map(versions, version => ({ text: `v${version}`, value: version }));

    if (versions.length === 1) return <span className="bold color-grey pr2">v{template.latest.v}</span>;

    return (
      <Dropdown
        className="color-grey"
        inline
        options={options}
        value={currentVersion}
        onChange={(e, data) => this.handleOnVersionChange(data)}
      />
    );
  }

  renderMainTabs(template) {
    const uiState = this.getState();
    return <WorkflowTemplateCardTabs template={template} uiState={uiState} />;
  }
}

export default inject('userDisplayName')(withRouter(observer(WorkflowTemplateCard)));
